<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <base-modal-comp
    tSel="resetPasswordModal"
    tag="form"
    :title="$t('auth.resetPassword.reset')"
    :actionText="$t('common.submit')"
    :waiting="saving || checkingPasswordStrength"
    @action="submit"
    @cancelled="close"
    @submit.native.prevent>
      <input-comp
        class="password"
        :id="formRefs.resetPassword"
        :name="formRefs.resetPassword"
        :ref="formRefs.resetPassword"
        type="password"
        :label="$t('auth.resetPassword.password12')"
        v-model="formData.newPassword"
        v-model:errorMessages="formData.resetPasswordErrors"
        :infoMessages="[passwordStrengthMessage]"
        :validationRules="[required, passwordTooShort]"
        @blur="checkPasswordStrength"
        @keyup="checkPasswordStrength"
      ></input-comp>
      <input-comp
        class="password-confirm"
        :id="formRefs.resetPasswordConfirm"
        :name="formRefs.resetPasswordConfirm"
        :ref="formRefs.resetPasswordConfirm"
        type="password"
        :label="$t('auth.resetPassword.confirm')"
        v-model="formData.passwordConfirm"
        v-model:errorMessages="formData.resetPasswordConfirmErrors"
        :validationRules="[required, passwordMatches(formData.newPassword)]"
        @input="checkPasswordMatch"
        :disabled="!formData.newPassword || formData.resetPasswordErrors.length > 0"
      ></input-comp>
  </base-modal-comp>
</template>

<script lang="ts">
import { putResetPasswordAsync } from '@/api/userApi';
import { isSuccess } from '@/utils/result';
import { errorToast, successToast } from '@/utils/notificationUtils';
import { required, formValidator, IErrorMessage } from '@/val/customValidation';
import passwordValidation from '@/val/passwordValidation';

export default {
  setup() {
    return {
      required,
      formValidator,
      ...passwordValidation,
    };
  },
  name: 'forgot-reset-modal',
  data() {
    return {
      formData: {
        newPassword: '',
        resetPasswordErrors: [] as IErrorMessage[],
        passwordConfirm: '',
        resetPasswordConfirmErrors: [] as IErrorMessage[],
      },
      formRefs: {
        resetPassword: 'resetPassword',
        resetPasswordConfirm: 'resetPasswordConfirm',
      },
      checkingPasswordStrength: false,
      saving: false,
    };
  },
  computed: {
    passwordIsStrong() {
      return this.formData.resetPasswordErrors.length === 0;
    },
    passwordStrengthMessage() {
      return this.passwordIsStrong
        ? this.$t('signUp.passwordStrong')
        : '';
    },
  },
  methods: {
    addPasswordValidationError(msg: string | null = null) {
      this.formData.resetPasswordErrors = [];

      if (msg) {
        this.formData.resetPasswordErrors.push({
          id: this.formRefs.resetPassword,
          validation: '',
          errorMessageText: msg,
        });
      }
    },
    checkPasswordMatch() {
      this.formValidator(this, [this.formRefs.resetPasswordConfirm]);
    },
    close() {
      this.$emit('close');
    },
    async checkPasswordStrength() {
      const passwordValidationResults = this.formValidator(this, [this.formRefs.resetPassword]);

      if (passwordValidationResults.length > 0 || this.checkingPasswordStrength) {
        return;
      }

      this.checkingPasswordStrength = true;

      const result = await this.checkPasswordValidity(
        this.$route?.query?.id as string,
        this.formData.newPassword);

      this.addPasswordValidationError(result?.value);

      this.checkingPasswordStrength = false;

      this.checkPasswordMatch();
    },
    async submit() {
      const validationMessages = this.formValidator(this, this.formRefs);

      if (validationMessages.length > 0) {
        return;
      }

      try {
        this.saving = true;

        const resetPasswordDto = {
          userId: this.$route.query.id as string,
          token: this.$route.query.token as string,
          newPassword: this.formData.newPassword,
        };

        const response = await putResetPasswordAsync(resetPasswordDto);

        if (isSuccess(response)) {
          successToast(this.$store, 'auth.resetPassword.success');
          this.close();
        } else {
          errorToast(this.$store, 'auth.resetPassword.error');
        }
      }
      finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.password-rule-tips {
  @include font-body-book-regular;
  color: $secondary-gray;
  width: 100%;
  margin-bottom: 30px;
}

.header-sub-title {
  color: $primary-gray;
  font-size: 18px;
  margin-bottom: 20px;
}
</style>
